/**
 * feed.ts â€” the public debate feed.
 *
 * Posts are copies of finished debates made by a Cloud Function, which the
 * client asks for through a `feedPostRequests` doc; the client only reads posts
 * and writes its own likes and reports.
 * See functions/src/feed.ts for the data model.
 */

import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  limit,
  onSnapshot,
  orderBy,
  query,
  QueryDocumentSnapshot,
  serverTimestamp,
  setDoc,
  startAfter,
  Timestamp,
  where,
} from "firebase/firestore";
import { auth, db } from "./config";
import { authHeaders } from "./authHeaders";
import { Verdict, parseVerdict } from "../utils/verdict";
import { DebateSource, parseSources } from "../utils/sources";

export type FeedSort = "newest" | "top" | "discussed";
export type ContentWarning = "language" | "sensitive" | null;
export type ReportReason = "spam" | "offensive" | "misleading" | "other";

export const FEED_PAGE_SIZE = 20;

/** What the person chose in the wizard when they asked to post a debate. */
export interface FeedShare {
  anonymous: boolean;
}

export interface FeedPost {
  id: string;
  question: string;
  experts: string[];
  debateMode: "single" | "multi";
  verdictExcerpt: string;
  /** The panel's yes/no or option split; absent on open-ended questions and older posts. */
  verdict: Verdict | null;
  authorName: string;
  contentWarning: ContentWarning;
  publishedAt: Timestamp | null;
  likeCount: number;
  commentCount: number;
}

export interface FeedContent {
  steps: any[];
  chaosSteps: any[];
  finalAggregate: string;
  sources?: DebateSource[];
}

export interface FeedComment {
  id: string;
  authorUid: string;
  authorName: string;
  text: string;
  contentWarning: ContentWarning;
  createdAt: Timestamp | null;
}

export interface FeedPage {
  posts: FeedPost[];
  cursor: QueryDocumentSnapshot | null;
}

const FUNCTIONS_BASE = "https://us-central1-familyaichat-1998.cloudfunctions.net";

const SORT_FIELD: Record<FeedSort, string> = {
  newest: "publishedAt",
  top: "likeCount",
  discussed: "commentCount",
};

function toPost(snap: { id: string; data: () => any }): FeedPost {
  const d = snap.data();
  return {
    id: snap.id,
    question: d.question ?? "",
    experts: d.experts ?? [],
    debateMode: d.debateMode === "single" ? "single" : "multi",
    verdictExcerpt: d.verdictExcerpt ?? "",
    verdict: parseVerdict(d.verdict),
    authorName: d.authorName ?? "Anonymous",
    contentWarning: d.contentWarning ?? null,
    publishedAt: d.publishedAt ?? null,
    likeCount: d.likeCount ?? 0,
    commentCount: d.commentCount ?? 0,
  };
}

export async function loadFeedPage(
  sort: FeedSort,
  cursor: QueryDocumentSnapshot | null = null,
): Promise<FeedPage> {
  const constraints = [
    where("hidden", "==", false),
    orderBy(SORT_FIELD[sort], "desc"),
    ...(sort === "newest" ? [] : [orderBy("publishedAt", "desc")]),
    ...(cursor ? [startAfter(cursor)] : []),
    limit(FEED_PAGE_SIZE),
  ];
  const snap = await getDocs(query(collection(db, "publicDebates"), ...constraints));
  return {
    posts: snap.docs.map(toPost),
    cursor: snap.docs.length === FEED_PAGE_SIZE ? snap.docs[snap.docs.length - 1] : null,
  };
}

/** Typo-tolerant search over question, panel and verdict, best match first. */
export async function searchFeed(q: string, signal?: AbortSignal): Promise<FeedPost[]> {
  const resp = await fetch(`${FUNCTIONS_BASE}/searchFeed?q=${encodeURIComponent(q)}`, { signal });
  if (!resp.ok) throw new FeedRequestError(`http_${resp.status}`);
  const data = await resp.json();
  return (data.posts ?? []).map((p: any) =>
    toPost({
      id: p.id,
      data: () => ({
        ...p,
        publishedAt: p.publishedAtMs == null ? null : Timestamp.fromMillis(p.publishedAtMs),
      }),
    }),
  );
}

/** One post, for permalinks. Resolves null when it was removed or hidden. */
export async function loadFeedPost(id: string): Promise<FeedPost | null> {
  try {
    const snap = await getDoc(doc(db, "publicDebates", id));
    return snap.exists() ? toPost(snap) : null;
  } catch (err: any) {
    // Hidden posts fail the read rule rather than coming back empty.
    if (err?.code === "permission-denied") return null;
    throw err;
  }
}

export async function loadFeedContent(id: string): Promise<FeedContent | null> {
  const snap = await getDoc(doc(db, "publicDebates", id, "content", "main"));
  if (!snap.exists()) return null;
  const d = snap.data();
  return {
    steps: d.steps ?? [],
    chaosSteps: d.chaosSteps ?? [],
    finalAggregate: d.finalAggregate ?? "",
    sources: parseSources(d.sources),
  };
}

export async function loadFeedComments(id: string): Promise<FeedComment[]> {
  const snap = await getDocs(
    query(
      collection(db, "publicDebates", id, "comments"),
      where("hidden", "==", false),
      orderBy("createdAt", "asc"),
    ),
  );
  return snap.docs.map((c) => {
    const d = c.data();
    return {
      id: c.id,
      authorUid: d.authorUid,
      authorName: d.authorName ?? "Anonymous",
      text: d.text ?? "",
      contentWarning: d.contentWarning ?? null,
      createdAt: d.createdAt ?? null,
    };
  });
}

export async function hasLiked(id: string, uid: string): Promise<boolean> {
  return (await getDoc(doc(db, "publicDebates", id, "likes", uid))).exists();
}

export async function setLiked(id: string, uid: string, liked: boolean): Promise<void> {
  const ref = doc(db, "publicDebates", id, "likes", uid);
  if (liked) await setDoc(ref, { createdAt: serverTimestamp() });
  else await deleteDoc(ref);
}

/** Resolves false when this person already reported it. */
export async function reportFeedItem(
  path: { postId: string; commentId?: string },
  uid: string,
  reason: ReportReason,
): Promise<boolean> {
  const segments = path.commentId
    ? ["publicDebates", path.postId, "comments", path.commentId, "reports", uid]
    : ["publicDebates", path.postId, "reports", uid];
  try {
    await setDoc(doc(db, segments[0], ...segments.slice(1)), {
      reason,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (err: any) {
    if (err?.code === "permission-denied") return false;
    throw err;
  }
}

export async function deleteFeedComment(postId: string, commentId: string): Promise<void> {
  await deleteDoc(doc(db, "publicDebates", postId, "comments", commentId));
}

export class FeedRequestError extends Error {
  constructor(public code: string) {
    super(code);
  }
}

export async function callFeedFunction(
  name: string,
  body: Record<string, unknown>,
  options: { keepalive?: boolean } = {},
) {
  const headers = await authHeaders();
  let resp: Response;
  try {
    resp = await fetch(`${FUNCTIONS_BASE}/${name}`, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      keepalive: options.keepalive,
    });
  } catch {
    throw new FeedRequestError("unreachable");
  }
  const data = await resp.json().catch(() => ({}));
  if (!resp.ok) throw new FeedRequestError(data.error || `http_${resp.status}`);
  return data;
}

/**
 * Asks for a debate to be posted and waits for the result. Once the request doc
 * is saved the post finishes on the server, even if this tab closes; a cold
 * start of the function can take over a minute, retries take longer.
 */
export function publishToFeed(debateId: string, anonymous: boolean): Promise<void> {
  const uid = auth.currentUser?.uid;
  if (!uid) return Promise.reject(new FeedRequestError("authentication_required"));
  const ref = doc(collection(db, "feedPostRequests"));
  return new Promise<void>((resolve, reject) => {
    let unsubscribe = () => {};
    const settle = (finish: () => void) => {
      window.clearTimeout(timer);
      unsubscribe();
      finish();
    };
    const timer = window.setTimeout(
      () => settle(() => reject(new FeedRequestError("post_slow"))),
      POST_WAIT_MS,
    );
    setDoc(ref, { uid, kind: "post", debateId, anonymous, status: "pending", createdAt: serverTimestamp() })
      .then(() => {
        unsubscribe = onSnapshot(
          ref,
          (snap) => {
            const status = snap.get("status");
            if (status === "done") settle(resolve);
            else if (status === "failed") settle(() => reject(new FeedRequestError(snap.get("error") || "publish_failed")));
          },
          () => settle(() => reject(new FeedRequestError("unreachable"))),
        );
      })
      .catch(() => settle(() => reject(new FeedRequestError("unreachable"))));
  });
}

const POST_WAIT_MS = 3 * 60 * 1000;
const WARM_EVERY_MS = 5 * 60 * 1000;
let lastWarm = 0;

/**
 * Starts the posting function if it has scaled to zero. A cold start can take
 * over a minute, so this runs when the share menu opens, ahead of the post.
 */
export function warmFeedPosting(): void {
  const uid = auth.currentUser?.uid;
  if (!uid || Date.now() - lastWarm < WARM_EVERY_MS) return;
  lastWarm = Date.now();
  setDoc(doc(collection(db, "feedPostRequests")), { uid, kind: "warm", createdAt: serverTimestamp() })
    .catch(() => undefined);
}

export async function unpublishFromFeed(debateId: string): Promise<void> {
  await callFeedFunction("unpublishDebate", { debateId }, { keepalive: true });
}

export async function postFeedComment(debateId: string, text: string): Promise<string> {
  return (await callFeedFunction("postFeedComment", { debateId, text })).commentId;
}

/** Whether the signed-in owner's debate is currently on the feed. */
export async function isPublishedToFeed(debateId: string): Promise<boolean> {
  try {
    return (await getDoc(doc(db, "debates", debateId))).get("publishedToFeed") === true;
  } catch {
    return false;
  }
}

/** Plain-language message for an error from the feed functions. */
export function feedErrorMessage(err: unknown): string {
  const code = err instanceof FeedRequestError ? err.code : "";
  switch (code) {
    case "content_flagged":
      return "This can't be posted publicly because it was flagged by moderation.";
    case "debate_not_finished":
      return "Wait for the debate to finish before posting it.";
    case "moderation_unavailable":
      return "The moderation check is unavailable. Try again in a minute.";
    case "authentication_required":
      return "Sign in to post to the feed.";
    case "post_not_found":
    case "debate_not_found":
      return "That post is no longer on the feed.";
    case "not_a_moderator":
      return "Only moderators can do that.";
    case "invalid_comment":
      return "Comments must be between 1 and 1,000 characters.";
    case "post_slow":
      return "This is taking longer than usual. It may still post, so check the feed in a few minutes.";
    case "unreachable":
      return "Couldn't reach the feed. Check your connection and try again.";
    default:
      return "Something went wrong. Try again.";
  }
}

// â”€â”€ Moderation (allowlisted admins; checked again on the server) â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€â”€

export interface ModerationItem {
  kind: "post" | "comment";
  postId: string;
  commentId?: string;
  question: string;
  text: string;
  authorName: string;
  hidden: boolean;
  reportCount: number;
  contentWarning: ContentWarning;
  reasons: Partial<Record<ReportReason, number>>;
}

export async function loadModerationQueue(): Promise<ModerationItem[]> {
  return (await callFeedFunction("moderateFeed", { action: "list" })).items ?? [];
}

export async function moderateFeedItem(
  target: { postId: string; commentId?: string },
  action: "restore" | "remove",
): Promise<void> {
  await callFeedFunction("moderateFeed", { action, ...target });
}
